/** Планы DP (минимум / максимум) за 2 недели по метрикам статистики MRP */

export const DP_PLAN_ACTIONS = [
  {
    key: 'punishments',
    label: 'Наказания',
    verb: 'Выдать',
    unit: 'наказаний (Discord + AS)',
    variant: 'punish',
    badge: 'Нак.',
  },
  {
    key: 'chat_responses',
    label: 'Ответы',
    verb: 'Дать',
    unit: 'ответов (Discord + AS)',
    variant: 'chat',
    badge: 'Отв.',
  },
  {
    key: 'deleted_comments_as',
    label: 'Удалённые комментарии AS',
    verb: 'Удалить',
    unit: 'комментариев AS',
    variant: 'afk',
    badge: 'Ком.',
  },
  {
    key: 'closed_tickets',
    label: 'Закрытые тикеты',
    verb: 'Закрыть',
    unit: 'тикетов',
    variant: 'hours',
    badge: 'Тик.',
  },
];

/** Метрики в UI выплат (стакают DS+AS) */
export const PAYOUT_DISPLAY_METRICS = [
  { key: 'punishments', label: 'Наказания', short: 'Нак.', group: 'pun' },
  { key: 'chat_responses', label: 'Ответы', short: 'Отв.', group: 'resp' },
  { key: 'deleted_comments_as', label: 'Удалённые комментарии AS', short: 'Ком.', group: 'mod' },
  { key: 'closed_tickets', label: 'Закрытые тикеты', short: 'Тик.', group: 'tickets' },
];

const emptyTargets = () =>
  DP_PLAN_ACTIONS.reduce((acc, action) => {
    acc[action.key] = 0;
    return acc;
  }, {});

export const DEFAULT_DP_PLAN_SETTINGS = {
  plan_minimum_dp: 0,
  plan_maximum_dp: 0,
  plan_minimum: emptyTargets(),
  plan_maximum: emptyTargets(),
};

/** Сумма сырых метрик недели → ключи плана (наказания/ответы = Discord + AS) */
export function aggregateDpPlanMetrics(metrics = {}) {
  const m = metrics && typeof metrics === 'object' ? metrics : {};
  const splitPun =
    (Number(m.punishments_discord) || 0) + (Number(m.punishments_as) || 0);
  const splitChat =
    (Number(m.chat_responses_discord) || 0) + (Number(m.chat_responses_as) || 0);
  const hasPunSplit = m.punishments_discord != null || m.punishments_as != null;
  const hasChatSplit = m.chat_responses_discord != null || m.chat_responses_as != null;

  return {
    punishments: hasPunSplit ? splitPun : (Number(m.punishments) || 0),
    chat_responses: hasChatSplit ? splitChat : (Number(m.chat_responses) || 0),
    deleted_comments_as: Number(m.deleted_comments_as) || 0,
    closed_tickets: Number(m.closed_tickets) || 0,
  };
}

function readLegacyStacked(raw, stackedKey, partA, partB) {
  if (raw[stackedKey] != null && raw[stackedKey] !== '') {
    return Math.max(0, parseInt(raw[stackedKey], 10) || 0);
  }
  return Math.max(0, (parseInt(raw[partA], 10) || 0) + (parseInt(raw[partB], 10) || 0));
}

function normalizeTargets(raw) {
  const base = emptyTargets();
  if (!raw || typeof raw !== 'object') return base;
  base.punishments = readLegacyStacked(raw, 'punishments', 'punishments_discord', 'punishments_as');
  base.chat_responses = readLegacyStacked(raw, 'chat_responses', 'chat_responses_discord', 'chat_responses_as');
  base.deleted_comments_as = Math.max(0, parseInt(raw.deleted_comments_as, 10) || 0);
  base.closed_tickets = Math.max(0, parseInt(raw.closed_tickets, 10) || 0);
  return base;
}

export function normalizeDpPlanSettings(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    plan_minimum_dp: Math.max(0, parseInt(src.plan_minimum_dp, 10) || 0),
    plan_maximum_dp: Math.max(0, parseInt(src.plan_maximum_dp, 10) || 0),
    plan_minimum: normalizeTargets(src.plan_minimum),
    plan_maximum: normalizeTargets(src.plan_maximum),
  };
}

/** План закрыт полностью: есть хотя бы одна цель, и все цели > 0 выполнены */
export function isDpPlanComplete(metrics = {}, targets = {}) {
  const aggregated = aggregateDpPlanMetrics(metrics);
  const required = DP_PLAN_ACTIONS.filter(({ key }) => (Number(targets[key]) || 0) > 0);
  if (required.length === 0) return false;
  return required.every(({ key }) => (Number(aggregated[key]) || 0) >= (Number(targets[key]) || 0));
}

/**
 * DP по планам: минимум + (дополнительно) максимум.
 * Частичное выполнение не даёт DP — только полный закрытый план.
 */
export function calculatePlanDp(metrics = {}, settingsInput = {}) {
  const settings = normalizeDpPlanSettings(settingsInput);
  let total = 0;
  if (isDpPlanComplete(metrics, settings.plan_minimum)) {
    total += settings.plan_minimum_dp;
  }
  if (isDpPlanComplete(metrics, settings.plan_maximum)) {
    total += settings.plan_maximum_dp;
  }
  return Math.max(0, total);
}

export function actionRequirementText(action, count) {
  const n = Math.max(0, Number(count) || 0);
  return `${action.verb} ${n} ${action.unit}`;
}

/** Безопасное число для процентов / бонусов (строки из инпутов не конкатятся) */
export function toFiniteNumber(value, fallback = 0) {
  if (value === '' || value === '-' || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}
